<script>
  // @ts-check
  // eslint-disable-next-line no-unused-vars
  import * as Types from "./globalFooterTypes.js";
  // eslint-disable-next-line no-unused-vars
  import * as Themes from "../../utilities/themes.js";

  import GridContainer from "../../atoms/GridContainer.svelte";
  import { getTheme } from "../../utilities/themes.js";
  import Grid from "../../atoms/Grid.svelte";
  import Link from "../../atoms/Link.svelte";
  import FooterColumn from "./FooterColumn.svelte";
  import Button from "../../atoms/Button.svelte";
  import Icon from "../../atoms/Icon.svelte";
  import DynamicHeadingContext from "../../atoms/DynamicHeadingContext.svelte";
  import DynamicHeading from "../../atoms/DynamicHeading.svelte";
  import Image from "../../atoms/Image.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /**
   * The main, large title text of the Footer
   * @type {string | Types.TitleContentItem[]}
   */
  export let title;

  /**
   * The copy under the main title
   * @type {string}
   */
  export let subTitle;

  /**
   * The text for the CTA button
   * @type {Types.CtaButton}
   */
  export let ctaButton;

  /**
   * Array of footer column links in the main body of the footer
   * @type {Types.FooterMainSection}
   */
  export let footerColumns;

  /**
   * Array of Social Media links
   * @type {Types.SocialLinks}
   */
  export let socialLinks;

  /**
   * Array of Legal links at the bottom of the footer, such as Terms of Service and Privacy Policy
   * @type {Types.LegalLink}
   */

  export let legalLinks;

  /**
   * @type {Themes.ThemeName}
   */
  export let themeName = "cobaltBlue";
  $: currentTheme = getTheme(themeName);

  /**
   * convert string title to content item array
   *
   * @param {string} title
   * @returns {Types.TitleContentItem[]}
   */
  const getFallbackTitle = (title) => [{ content: title, type: "text" }];

  /**
   * @param {string | Types.TitleContentItem[]} title
   * @returns {string}
   */
  const getAriaLabel = (title) =>
    Array.isArray(title)
      ? title
          .filter((c) => c.type === "text")
          .map((c) => c.content)
          .join(" ")
      : title;
</script>

<DynamicHeadingContext>
  <GridContainer
    {id}
    containerElement="footer"
    containerClass="{currentTheme?.textColor} {currentTheme?.backgroundColor} overflow-hidden py-15"
  >
    <DynamicHeading
      class="xl:gap=[37px] mb-6 flex w-full flex-wrap items-center gap-5 font-gotham-condensed text-[35vw] font-bold uppercase leading-extra-tight !tracking-[-2.48px] md:flex-nowrap md:whitespace-nowrap md:text-[16vw] lg:gap-[24px] lg:text-[16.25vw] xl:text-[16.5vw]"
      aria-label={getAriaLabel(title)}
    >
      {#each Array.isArray(title) ? title : getFallbackTitle(title) as contentItem}
        {#if contentItem?.type === "image"}
          <Image
            aspectRatioClasses="aspect-3/2"
            containerClass={contentItem.class}
            elementClass=""
            src={contentItem.content}
            srcset={contentItem.srcset}
            sizes={contentItem.sizes}
            alt=""
          />
        {:else if contentItem?.type === "text"}
          <span class={contentItem.class}>
            {contentItem.content}
          </span>
        {/if}
      {/each}
    </DynamicHeading>

    <p class="body-lg col-full-width mb-7 w-full">
      {subTitle}
    </p>
    <div>
      <Button
        theme={themeName}
        id={ctaButton.id}
        url={ctaButton.href}
        label={ctaButton.label}
        variant="filled"
        buttonClass="inline-flex"
      />
    </div>
    <Grid gridClass="col-full-width">
      {#each footerColumns as contentColumn}
        <FooterColumn
          type={contentColumn.type}
          links={contentColumn.links}
          card={contentColumn.card}
          title={contentColumn.title}
          {themeName}
          columnClass="mt-15"
        />
      {/each}

      <div class="col-span-11 mt-12.5 md:col-span-12 lg:col-span-9">
        <div class="body-sm">
          <slot name="bottomText" />
        </div>
        <div class="mt-4 flex gap-6 lg:mt-8">
          {#each legalLinks as legalLink}
            <Link
              class="body-sm hover:text-white"
              href={legalLink.href}
              label={legalLink.text}
              variant="inline"
              theme={themeName}
            />
          {/each}
        </div>
      </div>
      <div class="mt-12.5 flex gap-9">
        {#each socialLinks as socialItem}
          <a
            aria-label={socialItem.iconName}
            class="-m-4 h-fit w-fit p-4 hover:outline focus-visible:outline-dashed"
            data-sveltekit-reload
            href={socialItem.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name={socialItem.iconName} />
          </a>
        {/each}
      </div>
    </Grid>
  </GridContainer>
</DynamicHeadingContext>
